import { Button } from "@dropbox/dig-components/buttons";
import { Text, Title } from "@dropbox/dig-components/typography";
import { copyToClipboard, openURL } from "@mirage/service-platform-actions";
import type { SearchResult } from "@mirage/shared/search/search-result";
import { showSnackbar } from "@mirage/shared/snackbar";
import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./DevToolsPanel.module.css";

interface DevToolsPanelProps {
  selectedResult: SearchResult | null;
  onClose: () => void;
}

const MIN_HEIGHT = 200;
const MAX_HEIGHT = 600;
const DEFAULT_HEIGHT = 300;

export const DevToolsPanel = ({
  selectedResult,
  onClose,
}: DevToolsPanelProps) => {
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startHeight, setStartHeight] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);

  const handleCopy = useCallback(() => {
    copyToClipboard(JSON.stringify(selectedResult, null, 2));
    showSnackbar({ title: "Copied result to clipboard" });
  }, [selectedResult]);

  const handleOpen = useCallback(() => {
    if (!selectedResult?.url) return;
    openURL(selectedResult.url);
  }, [selectedResult]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      setStartY(e.clientY);
      setStartHeight(height);
    },
    [height],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaY = startY - e.clientY; // Negative delta = dragging down, positive = dragging up
      const newHeight = Math.min(
        MAX_HEIGHT,
        Math.max(MIN_HEIGHT, startHeight + deltaY),
      );
      setHeight(newHeight);
    },
    [isDragging, startY, startHeight],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.userSelect = "";
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!selectedResult) {
    return null;
  }

  return (
    <div
      ref={panelRef}
      className={styles.devToolsPanel}
      style={{ height: `${height}px` }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className={styles.resizeHandle}
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize panel height"
        onMouseDown={handleMouseDown}
      >
        <div className={styles.resizeBar} />
      </div>

      <div className={styles.header}>
        <Title size="small">Search Devtools</Title>
        <div className={styles.headerButtons}>
          <Button variant="opacity" size="small" onClick={handleOpen}>
            Open result
          </Button>
          <Button variant="opacity" size="small" onClick={handleCopy}>
            Copy Result
          </Button>
          <Button variant="opacity" size="small" onClick={onClose}>
            Exit
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.resultInfo}>
          <Text size="small" isBold>
            Title:
          </Text>
          <Text size="small">{selectedResult.title}</Text>
        </div>

        <div className={styles.resultInfo}>
          <Text size="small" isBold>
            Relevance score:
          </Text>
          <Text size="small" color="subtle">
            {selectedResult.relevanceScore}
          </Text>
        </div>

        <div className={styles.resultInfo}>
          <Text size="small" isBold>
            UUID:
          </Text>
          <Text size="small" color="subtle">
            {selectedResult.uuid}
          </Text>
        </div>

        <div className={styles.resultInfo}>
          <Text size="small" isBold>
            ID3p:
          </Text>
          <Text size="small" color="subtle">
            {selectedResult.id3p}
          </Text>
        </div>

        <div className={styles.resultInfo}>
          <Text size="small" isBold>
            Source:
          </Text>
          <Text size="small" color="subtle">
            {selectedResult.sourceIndexType?.[".tag"]}
          </Text>
        </div>
        <div className={styles.resultInfo}>
          <Text size="small" isBold>
            URL:
          </Text>
          <Text size="small" color="subtle">
            {selectedResult.url}
          </Text>
        </div>
      </div>
    </div>
  );
};
